import React, { Fragment } from 'react';
import styled from 'styled-components';

import { useQuery } from '@apollo/client';
import { useParams, useLocation } from 'react-router-dom';

import useConfig from 'app/config';
import GenericPage from 'app/ui/components/layouts/generic-page';

import ErrorComponent from 'app/ui/components/molecules/error';
import Loading from 'app/ui/components/molecules/loading';
import ResourceHeader from 'app/ui/components/molecules/resource-header';
import ResourceSections from 'app/ui/components/molecules/resource-sections';
import Breadcrumb from 'app/ui/components/molecules/breadcrumb';
import ResourceNavigation from 'app/ui/components/molecules/resource-navigation';
import MoreResourceContent from 'app/ui/components/molecules/more-resource-content';

import { ResourcePage as RESOURCE } from 'app/pages/resource/resource.gql';
import type { ResourcePageQuery, ResourcePageQueryVariables } from 'app/types/graphql';
import Button from 'src/app/ui/components/atoms/button';

const BreadcrumbWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
`;

const StyledBreadcrumb = styled(Breadcrumb)`
  display: none;

  ${({ theme: { media, spacing } }) =>
    `${media.medium(
      () => `
       margin: ${spacing(14)} 0 ${spacing(6)} 0;
       display: inline-flex;
       `,
    )}`}
`;

const CTALink = styled.a`
  ${({ theme: { spacing, media } }) => `
   width: 100%;
   margin: ${spacing(7)} 0 ${spacing(3)} 0;
  
  ${media.medium(
    () => `
  width: auto;
  margin: ${spacing(14)} 0 ${spacing(6)} 0;
  `,
  )}
  `}
`;

const CTAButton = styled(Button)`
  width: 100%;
`;

type ResourcePageProps = {
  resourceId: string;
  resourceListId?: string;
};

const ResourcePage: React.FC<ResourcePageProps> = ({ resourceId, resourceListId }: ResourcePageProps) => {
  const { config } = useConfig();
  const resourceListsEnabled = config.REACT_APP_RESOURCE_LISTS_ENABLED;

  const { data, loading, error } = useQuery<ResourcePageQuery, ResourcePageQueryVariables>(RESOURCE, {
    variables: { id: resourceId },
  });

  if (loading || !data?.resourceById) {
    return <Loading />;
  }

  if (error) {
    return <ErrorComponent />;
  }

  const { title, content, image, sections = [], id, ctaLink, ctaLabel } = data.resourceById;
  const hasCTA = ctaLink && ctaLabel;

  return (
    <>
      <GenericPage pageTitle={`Resource - ${title}`}>
        <ResourceHeader title={title} content={content} imageUrl={image?.url || image?.signedUrl} />
        <BreadcrumbWrapper>
          <StyledBreadcrumb contentTitle={title} />
          {hasCTA && (
            <CTALink href={ctaLink} target="_blank">
              <CTAButton label={ctaLabel} />
            </CTALink>
          )}
        </BreadcrumbWrapper>
        <ResourceSections sections={sections || []} resourceId={id} />

        {resourceListsEnabled && resourceListId && (
          <ResourceNavigation resourceListId={resourceListId} currentResourceId={resourceId} />
        )}

        {resourceListsEnabled && <MoreResourceContent limit={3} />}
      </GenericPage>
    </>
  );
};

const Resource: React.FC = () => {
  const { id: resourceId } = useParams();
  const location = useLocation();

  const resourceListId = React.useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    return searchParams.get('resourceListId') || undefined;
  }, [location.search]);

  if (!resourceId) {
    return <ErrorComponent />;
  }

  return <ResourcePage resourceId={resourceId} resourceListId={resourceListId} />;
};

export default Resource;
